import { LoaderError } from "@citychallenge/core";
import { Component, ErrorInfo, FC, lazy } from "react";
import { useLocation } from "react-router-dom";

import styles from "./dummy-ui.module.scss";

const ErrorView = lazy(() => import("../partials/error-view/error-view"));

export interface SuspenseErrorBoundaryProps {
  hideError?: boolean;
  children: React.ReactNode;
  className?: string;
  /**
   * Runs before the boundary resets on "try again". The reset itself only
   * clears `Loadable` errors; a caller whose data suspends through another
   * cache (suspend-react keeps rejections forever) uses this to forget them,
   * or the retry re-throws what it just showed.
   */
  onRetry?: () => void;
}

export class SuspenseErrorBoundary extends Component<
  SuspenseErrorBoundaryProps,
  {
    error?: LoaderError;
  }
> {
  public state = {
    error: undefined as LoaderError | undefined,
  };

  constructor(props: SuspenseErrorBoundaryProps) {
    super(props);
  }

  public componentDidCatch(error: LoaderError, { componentStack }: ErrorInfo) {
    if (!(error as any)?.doNotCapture) {
      // Route through the lazy shim so we share the single "sentry" chunk
      // instead of webpack creating a second one for this inline import().
      import("../lazy-sentry").then((Sentry) => {
        Sentry.captureException(error, {
          contexts: { react: { componentStack } },
        });
      });
    }

    this.setState({ error });
  }

  public render() {
    if (this.state.error) {
      if (this.props.hideError) return null;
      // You can render any custom fallback UI

      const error = (
        <ErrorView
          className={this.props.className}
          error={this.state.error}
          onRetry={() => {
            this.props.onRetry?.();
            this.state.error?.loadable?.setError(undefined);
            this.setState({ error: undefined });
          }}
        />
      );

      return <ErrorUI>{error}</ErrorUI>;
    }

    return this.props.children;
  }
}

export const ErrorUI: FC = ({ children }) => {
  const location = useLocation();

  const isGame =
    /\/gm_/i.test(location.pathname) || /_hotlink/i.test(location.pathname);

  if (isGame)
    return (
      <div
        data-theme="dark"
        className={`${styles.gameLoader} ${styles.darkUI}`}
      >
        {children}
      </div>
    );
  return <>{children}</>;
};
